import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import {
  UserPayoutRequest,
  useUserPayoutRequestsQuery,
} from '@admin/queries/AdminUserPayoutRequestsQueries';

import { useTipEntertainer } from '@hooks/EntertainerHooks/useTipEntertainer';

import { formatCentsAmountToDollars } from '@utilities/formatCentsAmountToDollars';

import Button from '@components/buttons/Button';
import Card from '@components/layout/Card/Card';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';

import { VenmoLogo } from '../../assets/VenmoLogo';

const WalletLedgerItem = ({
  walletLedgerItem,
}: {
  walletLedgerItem: UserPayoutRequest['walletLedger'][number];
}) => {
  const amountColor = walletLedgerItem.type === 'withdrawal' ? '#ff0000' : '#00ff00';
  const amountText = `${walletLedgerItem.type === 'withdrawal' ? '-' : '+'} ${formatCentsAmountToDollars(walletLedgerItem.amount)}`;

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            {dayjs(walletLedgerItem.createdAt).format('MM/DD/YYYY hh:mm A')}
          </div>
          <div>
            <strong>{capitalize(walletLedgerItem.type)}</strong>
          </div>
        </div>
        <div>
          <PopoverWrapper
            PopoverContent={({}) => (
              <div>
                <pre>{JSON.stringify(walletLedgerItem, null, 2)}</pre>
              </div>
            )}
          >
            <div style={{ color: amountColor }}>{amountText}</div>
          </PopoverWrapper>
        </div>
      </div>
    </div>
  );
};

const UserPayoutCard = ({ userPayout }: { userPayout: UserPayoutRequest }) => {
  const { handleVenmoEntertainer } = useTipEntertainer();

  const latestTransaction = userPayout.walletLedger?.[0];
  const isLatestTransactionWithdrawal = latestTransaction?.type === 'withdrawal';

  return (
    <Card>
      <Card.Header
        title={userPayout.entertainer?.title}
        action={
          isLatestTransactionWithdrawal && (
            <Button
              roundness="rounded"
              size="sm"
              className="ps-2 pe-3"
              startIcon={
                <div style={{ width: 18, height: 18 }}>
                  <VenmoLogo />
                </div>
              }
              onClick={() =>
                handleVenmoEntertainer({
                  entertainerId: userPayout.entertainer?._id,
                  venmo: userPayout.entertainer?.contact?.venmo,
                  amount: latestTransaction.amount / 100,
                  message: `${dayjs().format('MMM D, YYYY')} cash out courtesy of Colorado Drag Guide`,
                })
              }
            >
              Send a Tip
            </Button>
          )
        }
        subtitle={userPayout.entertainer?.contact?.venmo}
      />
      <Card.Body>
        <div className="d-grid gap-3">
          {userPayout.walletLedger.map((walletLedgerItem) => (
            <WalletLedgerItem key={walletLedgerItem._id} walletLedgerItem={walletLedgerItem} />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export const SuperAdminUserPayoutRequestsScreen = () => {
  const { userPayoutRequests, userPayoutRequestsAreReady } = useUserPayoutRequestsQuery();

  return (
    <Screen
      name="Super Admin User Payout Requests Screen"
      headerProps={{
        title: 'User Wallet Ledgers',
      }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425}>
        {userPayoutRequestsAreReady ? (
          <div>
            {userPayoutRequests.map((userPayout) => {
              return <UserPayoutCard key={userPayout.userId} userPayout={userPayout} />;
            })}
          </div>
        ) : (
          <>
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </>
        )}
      </Screen.Content>
    </Screen>
  );
};
